import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @typedef {object} CartItemsRenderEventData
 * @property {Promise<unknown>[]} [cartItemsRenderPromises]
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #pendingAutoOpenToken = 0;

  /** @type {number} */
  #openScrollY = 0;

  /** @type {{ scrollY: number; scrollRestoration: History['scrollRestoration'] } | null} */
  #pendingHistoryRestore = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('popstate', this.#handleInternalHistoryPopState, { capture: true });
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('popstate', this.#handleInternalHistoryPopState, { capture: true });
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#openScrollY = this.#getLockedScrollY();

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
  };

  #handleHistoryClose = () => {
    if (history.state?.cartDrawerOpen) {
      const scrollY = this.#openScrollY;
      const scrollRestoration = history.scrollRestoration;

      history.scrollRestoration = 'manual';
      this.#pendingHistoryRestore = { scrollY, scrollRestoration };
      history.back();
    }
  };

  #handleInternalHistoryPopState = (event) => {
    const pendingHistoryRestore = this.#pendingHistoryRestore;
    if (pendingHistoryRestore) {
      this.#pendingHistoryRestore = null;
      // This popstate only removes the cart drawer's synthetic history entry.
      event.stopImmediatePropagation();
      this.#restoreScrollAfterHistoryTraversal(pendingHistoryRestore);
      return;
    }

    if (!isMobileBreakpoint() || !this.refs.dialog?.open || history.state?.cartDrawerOpen) return;

    // Mobile Back should close the cart drawer without notifying the PLP search app.
    event.stopImmediatePropagation();
    const scrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
    this.closeDialog()
      .then(() => {
        this.#restoreScrollAfterHistoryTraversal({
          scrollY: this.#openScrollY,
          scrollRestoration,
        });
      })
      .finally(() => {
        this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
      });
  };

  /**
   * @param {{ scrollY: number; scrollRestoration: History['scrollRestoration'] }} restoreState
   */
  #restoreScrollAfterHistoryTraversal(restoreState) {
    const { scrollY, scrollRestoration } = restoreState;
    const restoreScroll = () => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    };

    requestAnimationFrame(restoreScroll);
    setTimeout(restoreScroll);
    setTimeout(() => {
      restoreScroll();
      history.scrollRestoration = scrollRestoration;
    }, 50);
  }

  #getLockedScrollY() {
    const bodyTop = Number.parseFloat(document.body.style.top || '0');

    return bodyTop < 0 ? Math.abs(bodyTop) : window.scrollY;
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open') || event.detail?.data?.didError) return;

    const autoOpenToken = ++this.#pendingAutoOpenToken;
    const eventData = /** @type {CartItemsRenderEventData | undefined} */ (event.detail?.data);

    if (eventData && !Array.isArray(eventData.cartItemsRenderPromises)) {
      eventData.cartItemsRenderPromises = [];
    }

    await Promise.resolve();

    const renderPromises = eventData?.cartItemsRenderPromises;
    if (Array.isArray(renderPromises) && renderPromises.length > 0) {
      await Promise.allSettled(renderPromises);
    }

    if (autoOpenToken !== this.#pendingAutoOpenToken) return;

    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
